import React, { useEffect, useState, useMemo } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Send, Receipt } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function PagosProveedores() {
  const [facturas, setFacturas] = useState([]);
  const [boletas, setBoletas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({}); // {docKey: monto}  docKey = `${tipo}:${id}`
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cuenta_bancaria_id: "",
    fecha: todayISO(),
    medio_pago: "transferencia",
    n_doc: "",
    glosa: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/pagos/facturas-pendientes"),
        api.get("/pagos"),
        api.get("/cuentas-bancarias"),
      ]);
      setFacturas(a.data.facturas || []);
      setBoletas(a.data.boletas_honorarios || []);
      setPagos(b.data.pagos || []);
      setCuentas(c.data || []);
      if (c.data?.[0] && !form.cuenta_bancaria_id) setForm((f) => ({ ...f, cuenta_bancaria_id: c.data[0].id }));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const toggle = (tipo, doc) => {
    const key = `${tipo}:${doc.id}`;
    setSel((s) => {
      if (s[key] !== undefined) { const { [key]: _, ...rest } = s; return rest; }
      return { ...s, [key]: doc.saldo_pendiente };
    });
  };

  const updateMonto = (tipo, doc, val) => {
    const key = `${tipo}:${doc.id}`;
    const m = parseFloat(val) || 0;
    setSel((s) => ({ ...s, [key]: m }));
  };

  const items = useMemo(() => {
    const arr = [];
    Object.entries(sel).forEach(([k, monto]) => {
      const [tipo, id] = k.split(":");
      if (monto > 0) arr.push({ tipo: tipo === "f" ? "factura_compra" : "boleta_honorarios", doc_id: id, monto });
    });
    return arr;
  }, [sel]);

  const total = items.reduce((s, it) => s + it.monto, 0);

  const submit = async () => {
    if (items.length === 0) { toast.warning("Selecciona al menos un documento"); return; }
    if (!form.cuenta_bancaria_id) { toast.warning("Selecciona cuenta bancaria"); return; }
    try {
      await api.post("/pagos", { ...form, items });
      toast.success(`Pago registrado · ${items.length} doc(s) · ${fmtCLP(total)}`);
      setOpen(false); setSel({});
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Page>
      <PageHeader
        title="Pagos a proveedores"
        subtitle="Liquida facturas de compra y boletas de honorarios"
        testid="pp-header"
        actions={
          <button onClick={() => setOpen(true)} disabled={items.length === 0}
            data-testid="pp-pay-button"
            className="inline-flex items-center gap-1.5 bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm">
            <Send className="h-4 w-4" /> Pagar seleccionados ({items.length})
          </button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI label="Facturas pendientes" value={facturas.length} />
        <KPI label="Boletas pendientes" value={boletas.length} />
        <KPI label="Seleccionados" value={items.length} tone="primary" />
        <KPI label="Total a pagar" value={fmtCLP(total)} tone="primary" />
      </div>

      <h3 className="font-display text-lg mb-2 mt-2">Facturas de compra pendientes</h3>
      <div className="bg-card border border-border rounded-md overflow-x-auto mb-6">
        {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
          : facturas.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Sin facturas pendientes ✓</div>
          : (
          <table className="acc-table" data-testid="pp-facturas">
            <thead><tr>
              <th style={{ width: 30 }}></th><th>Fecha</th><th>Folio</th><th>Proveedor</th>
              <th className="num">Total</th><th className="num">Pagado</th><th className="num">Saldo</th><th className="num">Monto a pagar</th>
            </tr></thead>
            <tbody>
              {facturas.map((f) => {
                const k = `f:${f.id}`; const checked = sel[k] !== undefined;
                return (
                  <tr key={f.id} className={checked ? "bg-primary/5" : ""}>
                    <td><input type="checkbox" checked={checked} onChange={() => toggle("f", f)} data-testid={`pp-check-f-${f.id}`} /></td>
                    <td>{fmtDate(f.fecha_emision)}</td>
                    <td className="font-mono text-[12px]">#{f.folio}</td>
                    <td>{f.contacto_nombre}</td>
                    <td className="num">{fmtCLP(f.total)}</td>
                    <td className="num text-[12px] text-muted-foreground">{fmtCLP(f.pagado_monto || 0)}</td>
                    <td className="num font-semibold">{fmtCLP(f.saldo_pendiente)}</td>
                    <td className="num">
                      {checked && (
                        <input type="number" value={sel[k]} onChange={(e) => updateMonto("f", f, e.target.value)}
                          className="w-28 px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {boletas.length > 0 && (
        <>
          <h3 className="font-display text-lg mb-2 mt-2">Boletas de honorarios pendientes</h3>
          <div className="bg-card border border-border rounded-md overflow-x-auto mb-6">
            <table className="acc-table" data-testid="pp-boletas">
              <thead><tr>
                <th style={{ width: 30 }}></th><th>Fecha</th><th>Folio</th><th>Prestador</th>
                <th className="num">Líquido</th><th className="num">Pagado</th><th className="num">Saldo</th><th className="num">Monto a pagar</th>
              </tr></thead>
              <tbody>
                {boletas.map((b) => {
                  const k = `b:${b.id}`; const checked = sel[k] !== undefined;
                  return (
                    <tr key={b.id} className={checked ? "bg-primary/5" : ""}>
                      <td><input type="checkbox" checked={checked} onChange={() => toggle("b", b)} data-testid={`pp-check-b-${b.id}`} /></td>
                      <td>{fmtDate(b.fecha_emision)}</td>
                      <td className="font-mono text-[12px]">#{b.folio}</td>
                      <td>{b.prestador_nombre}</td>
                      <td className="num">{fmtCLP(b.monto_liquido)}</td>
                      <td className="num text-[12px] text-muted-foreground">{fmtCLP(b.pagado_monto || 0)}</td>
                      <td className="num font-semibold">{fmtCLP(b.saldo_pendiente)}</td>
                      <td className="num">
                        {checked && (
                          <input type="number" value={sel[k]} onChange={(e) => updateMonto("b", b, e.target.value)}
                            className="w-28 px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Historial */}
      <h3 className="font-display text-lg mb-2 mt-2">Historial de pagos</h3>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {pagos.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">Sin pagos registrados aún</div> : (
          <table className="acc-table" data-testid="pp-historial">
            <thead><tr>
              <th>Fecha</th><th>Asiento</th><th>Medio</th><th>N° doc</th><th>Docs</th><th>Glosa</th><th className="num">Monto</th>
            </tr></thead>
            <tbody>
              {pagos.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.fecha)}</td>
                  <td className="font-mono text-[11px]"><Receipt className="inline h-3 w-3 mr-1" />#{p.asiento_numero}</td>
                  <td className="capitalize text-[12px]">{p.medio_pago}</td>
                  <td className="font-mono text-[11px]">{p.n_doc || "—"}</td>
                  <td className="text-[12px]">{(p.items || []).length} doc(s)</td>
                  <td className="text-[12px]">{p.glosa}</td>
                  <td className="num font-semibold">{fmtCLP(p.monto_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar pago</DialogTitle>
            <DialogDescription>Se generará un asiento contable con DEBE en cada documento pagado y HABER en la cuenta bancaria.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Cuenta bancaria</label>
                <Select value={form.cuenta_bancaria_id} onValueChange={(v) => setForm({ ...form, cuenta_bancaria_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.banco} · {c.numero}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">Fecha</label>
                <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Medio</label>
                <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">N° doc / cheque</label>
                <input value={form.n_doc} onChange={(e) => setForm({ ...form, n_doc: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1">Glosa</label>
              <input value={form.glosa} onChange={(e) => setForm({ ...form, glosa: e.target.value })}
                placeholder="(opcional) — se genera automáticamente si vacío"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div className="bg-muted rounded-sm p-3 text-sm flex justify-between">
              <span><strong>{items.length}</strong> documento(s) a pagar</span>
              <span className="font-semibold font-mono">{fmtCLP(total)}</span>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="border border-border hover:bg-muted text-sm px-4 py-2 rounded-sm">Cancelar</button>
            <button type="button" onClick={submit} data-testid="pp-confirm"
              className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
              Registrar pago
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-foreground", primary: "text-primary" };
  return (
    <div className="bg-card border border-border rounded-md p-3 sm:p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
